import React, { useState, useRef, useEffect } from 'react';
import { 
  User, 
  Camera, 
  Upload, 
  Save,
  Mail,
  Phone,
  MapPin,
  Globe,
  X,
  Check,
  CheckCircle
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { useLanguage, LANGUAGES } from '../../hooks/useLanguage';
import axios from 'axios';
import { BARBER_AVATARS, CLIENT_AVATARS, getUserAvatarUrl, getBarbershopAvatarUrl } from '../../lib/avatars';
import { useGooglePlaces } from '../../hooks/useGooglePlaces';

export const ProfileManagement = ({ user, barbershop, onUpdate, BACKEND_URL, onNotify, googleMapsApiKey }) => {
  const { t, currentLanguage, changeLanguage } = useLanguage();
  const [isEditing, setIsEditing] = useState(true);
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [addressError, setAddressError] = useState('');
  const fileInputRef = useRef(null);
  const { isLoaded, inputRef, setupPlaceChangedListener } = useGooglePlaces(googleMapsApiKey);
  const [selectedPlace, setSelectedPlace] = useState(null);
  
  const splitName = () => {
    if (user?.first_name || user?.last_name || user?.nickname) {
      return {
        firstName: user?.first_name || '',
        lastName: user?.last_name || '',
        nickname: user?.nickname || ''
      };
    }
    const parts = (user?.name || '').trim().split(/\s+/).filter(Boolean);
    return {
      firstName: parts[0] || '',
      lastName: parts.slice(1).join(' '),
      nickname: user?.nickname || ''
    };
  };

  const { firstName, lastName, nickname } = splitName();

  const buildFormData = () => ({
    firstName: user?.first_name || firstName,
    lastName: user?.last_name || lastName,
    nickname: user?.nickname || nickname,
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    address: user?.address || '',
    city: user?.city || '',
    comuna: user?.comuna || '',
    region: user?.region || '',
    street: user?.street || '',
    street_number: user?.street_number || '',
    postal_code: user?.postal_code || '',
    lat: typeof user?.lat === 'number' ? user.lat : null,
    lng: typeof user?.lng === 'number' ? user.lng : null,
    bio: user?.bio || '',
    age: user?.age || '',
    rut: user?.rut || '',
    instagram: user?.instagram || '',
    description: user?.description || ''
  });

  const [formData, setFormData] = useState(buildFormData());

  useEffect(() => {
    if (!isLoaded) return;
    setupPlaceChangedListener((place) => {
      const components = place.components || {};
      setSelectedPlace(place);
      setAddressError('');
      setFormData((prev) => ({
        ...prev,
        address: place.formatted_address,
        lat: place.lat,
        lng: place.lng,
        city: components.city || '',
        comuna: components.comuna || components.city || '',
        region: components.region || components.state || '',
        street: components.street_name || '',
        street_number: components.street_number || '',
        postal_code: components.postal_code || ''
      }));
    });
  }, [isLoaded, setupPlaceChangedListener]);

  const notify = (title, description, variant = 'info') => {
    if (onNotify) {
      onNotify(title, description, variant);
      return;
    }
    console.error(title, description);
  };

  const handleAvatarSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        notify('Archivo inválido', 'La imagen debe ser menor a 5MB', 'destructive');
        return;
      }
      
      setAvatarFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = () => {
        setAvatarPreview(reader.result);
      };
      reader.readAsDataURL(file);
      
      setShowAvatarModal(true);
    }
  };

  const handleAvatarUpload = async () => {
    if (!avatarFile) return;

    try {
      setLoading(true);

      const token = localStorage.getItem('token');
      let imageUrl = null;

      try {
        const authRes = await axios.get(`${BACKEND_URL}/api/imagekit/auth`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        const uploadData = new FormData();
        uploadData.append('file', avatarFile);
        uploadData.append('fileName', `${Date.now()}-${avatarFile.name}`);
        uploadData.append('publicKey', authRes.data.publicKey);
        uploadData.append('signature', authRes.data.signature);
        uploadData.append('expire', authRes.data.expire);
        uploadData.append('token', authRes.data.token);
        uploadData.append('folder', '/profiles');

        const uploadRes = await axios.post('https://upload.imagekit.io/api/v1/files/upload', uploadData);
        imageUrl = uploadRes.data.url;
      } catch (imageKitError) {
        console.warn('ImageKit error, fallback to local upload:', imageKitError);
      }

      if (imageUrl) {
        await axios.put(`${BACKEND_URL}/api/auth/profile`, {
          profile_image: imageUrl
        }, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } else {
        const fallbackData = new FormData();
        fallbackData.append('file', avatarFile);
        const fallbackRes = await axios.post(`${BACKEND_URL}/api/user/avatar`, fallbackData, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        imageUrl = fallbackRes.data.profile_image || fallbackRes.data.avatar_url;
      }

      if (imageUrl) {
        onUpdate({ ...user, profile_image: imageUrl });
      } else {
        notify('Error al subir', 'No se pudo subir la imagen. Revisa ImageKit.', 'destructive');
      }
      setShowAvatarModal(false);
      setAvatarFile(null);
      setAvatarPreview(null);
      
    } catch (error) {
      console.error('Error uploading avatar:', error);
      notify('Error al subir', 'Inténtalo nuevamente.', 'destructive');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    try {
      setLoading(true);
      
      const token = localStorage.getItem('token');
      const addressUnchanged = formData.address === (user?.address || '');
      const hasCoords = Number.isFinite(formData.lat) && Number.isFinite(formData.lng);
      const addressValid = !!selectedPlace || (addressUnchanged && hasCoords);
      if (!addressValid) {
        setAddressError('Debes seleccionar una dirección válida desde Google.');
        notify('Dirección inválida', 'Selecciona una dirección real desde Google.', 'destructive');
        setLoading(false);
        return;
      }
      const fullName = [formData.firstName, formData.lastName].filter(Boolean).join(' ').trim();
      const response = await axios.put(`${BACKEND_URL}/api/auth/profile`, {
        ...formData,
        name: fullName || formData.name,
        first_name: formData.firstName,
        last_name: formData.lastName,
        nickname: formData.nickname
      }, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      onUpdate({ ...user, ...formData, name: fullName || formData.name });
      setIsEditing(false);
      
    } catch (error) {
      console.error('Error updating profile:', error);
      notify('Error al guardar', 'No se pudo actualizar el perfil.', 'destructive');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Profile Header */}
      <Card className="bg-gray-900 border-gray-700">
        <CardHeader className="text-center">
          <div className="relative inline-block">
            <Avatar className="w-24 h-24 mx-auto">
              <AvatarImage
                src={user?.user_type === 'barber' && barbershop
                  ? getBarbershopAvatarUrl(barbershop)
                  : getUserAvatarUrl(user)}
                alt={user?.name}
              />
              <AvatarFallback className="bg-amber-600 text-white text-2xl">
                {user?.name?.[0]?.toUpperCase()}
              </AvatarFallback>
            </Avatar>
            
            <Button
              onClick={() => fileInputRef.current?.click()}
              size="sm"
              className="absolute -bottom-2 -right-2 rounded-full bg-amber-600 hover:bg-amber-700 w-8 h-8 p-0"
            >
              <Camera className="w-4 h-4" />
            </Button>
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleAvatarSelect}
              className="hidden"
            />
          </div>
          
          <CardTitle className="text-white text-xl mt-4">{user?.name}</CardTitle>
          <p className="text-gray-400">{user?.user_type === 'barber' ? 'Barbero Profesional' : 'Cliente'}</p>
          
          {/* Language Selector */}
          <div className="flex items-center justify-center gap-2 mt-4">
            <Globe className="w-4 h-4 text-gray-400" />
            <Select value={currentLanguage} onValueChange={changeLanguage}>
              <SelectTrigger className="w-32 bg-gray-800 border-gray-700 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.values(LANGUAGES).map((lang) => (
                  <SelectItem 
                    key={lang.code} 
                    value={lang.code}
                    className="text-white hover:bg-gray-700"
                  >
                    <span className="flex items-center gap-2">
                      <span>{lang.flag}</span>
                      <span>{lang.name}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
      </Card>

      {/* Profile Information */}
      <Card className="bg-gray-900 border-gray-700">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">{t('common.profile_info', 'Editar perfil')}</CardTitle>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between rounded-lg border border-gray-800 bg-gray-950/40 p-4">
            <div>
              <p className="text-sm text-gray-400">Foto de perfil</p>
              <p className="text-xs text-gray-500">Sube tu foto o elige un avatar profesional.</p>
            </div>
            <div className="flex items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="border-gray-700 text-gray-200">
                    Elegir avatar
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  {(user?.user_type === 'barber' ? BARBER_AVATARS : CLIENT_AVATARS).map((avatarUrl, index) => (
                    <DropdownMenuItem
                      key={avatarUrl}
                      onClick={async () => {
                        try {
                          setLoading(true);
                          const token = localStorage.getItem('token');
                          await axios.put(`${BACKEND_URL}/api/auth/profile`, {
                            profile_image: avatarUrl
                          }, {
                            headers: { 'Authorization': `Bearer ${token}` }
                          });
                          onUpdate({ ...user, profile_image: avatarUrl });
                        } catch (error) {
                          console.error('Error updating avatar:', error);
                          notify('Error', 'No se pudo actualizar el avatar.', 'destructive');
                        } finally {
                          setLoading(false);
                        }
                      }}
                      className="cursor-pointer text-white"
                    >
                      <div className="flex items-center gap-2">
                        <img src={avatarUrl} alt={`Avatar ${index + 1}`} loading="lazy" decoding="async" className="w-6 h-6 rounded-full object-cover" />
                        <span>Avatar {index + 1}</span>
                      </div>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <Button onClick={() => fileInputRef.current?.click()} variant="outline" className="border-amber-500 text-amber-300 hover:bg-amber-500/20">
                Subir foto
              </Button>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="text-white text-sm font-medium mb-2 block">
                Nombre
              </label>
              <Input
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                disabled={false}
              />
            </div>
            <div>
              <label className="text-white text-sm font-medium mb-2 block">
                Apellido
              </label>
              <Input
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                disabled={false}
              />
            </div>
            <div>
              <label className="text-white text-sm font-medium mb-2 block">
                Apodo
              </label>
              <Input
                value={formData.nickname}
                onChange={(e) => setFormData({ ...formData, nickname: e.target.value })}
                className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                disabled={false}
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-white text-sm font-medium mb-2 block">
                <Mail className="w-4 h-4 inline mr-2" />
                {t('auth.email')}
              </label>
              <Input
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                disabled={false}
                type="email"
              />
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-white text-sm font-medium mb-2 block">
                <Phone className="w-4 h-4 inline mr-2" />
                {t('barbershop.phone')}
              </label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                disabled={false}
                placeholder="+56 9 xxxx xxxx"
              />
            </div>
            
            <div>
              <label className="text-white text-sm font-medium mb-2 block">
                <MapPin className="w-4 h-4 inline mr-2" />
                {t('barbershop.address')}
              </label>
              <Input
                ref={inputRef}
                value={formData.address}
                onChange={(e) => {
                  setFormData({ ...formData, address: e.target.value });
                  setSelectedPlace(null);
                  setAddressError('');
                }}
                className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                disabled={false}
                placeholder="Tu dirección"
                autoComplete="off"
              />
              <div className="mt-2 flex items-center gap-2 text-xs text-gray-400">
                {selectedPlace || (formData.address === (user?.address || '') && Number.isFinite(formData.lat) && Number.isFinite(formData.lng)) ? (
                  <>
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    <span>Dirección validada</span>
                  </>
                ) : (
                  <span>Selecciona una dirección real desde la lista.</span>
                )}
              </div>
              {addressError && (
                <p className="text-xs text-red-400 mt-1">{addressError}</p>
              )}
            </div>
          </div>
          
          {user?.user_type === 'barber' && (
            <div>
              <label className="text-white text-sm font-medium mb-2 block">Biografía profesional</label>
              <textarea
                value={formData.bio}
                onChange={(e) => setFormData({...formData, bio: e.target.value})}
                className="w-full bg-gray-800 border border-gray-700 text-white placeholder-gray-400 rounded-md p-3 min-h-[80px]"
                disabled={false}
                placeholder="Cuéntanos sobre tu experiencia como barbero..."
              />
            </div>
          )}

          {user?.user_type === 'client' && (
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-white text-sm font-medium mb-2 block">Edad</label>
                  <Input
                    value={formData.age}
                    onChange={(e) => setFormData({...formData, age: e.target.value})}
                    className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                    placeholder="Ej: 28"
                  />
                </div>
                <div>
                  <label className="text-white text-sm font-medium mb-2 block">RUT</label>
                  <Input
                    value={formData.rut}
                    onChange={(e) => setFormData({...formData, rut: e.target.value})}
                    className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                    placeholder="12.345.678-9"
                  />
                </div>
              </div>
              <div>
                <label className="text-white text-sm font-medium mb-2 block">Instagram</label>
                <Input
                  value={formData.instagram}
                  onChange={(e) => setFormData({...formData, instagram: e.target.value})}
                  className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                  placeholder="@usuario"
                />
              </div>
              <div>
                <label className="text-white text-sm font-medium mb-2 block">Descripción</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full bg-gray-800 border border-gray-700 text-white placeholder-gray-400 rounded-md p-3 min-h-[80px]"
                  placeholder="Cuéntanos un poco sobre ti..."
                />
              </div>
            </div>
          )}
          
          <div className="flex gap-2">
            <Button 
              onClick={handleSaveProfile}
              disabled={loading}
              className="flex-1 bg-green-600 hover:bg-green-700"
            >
              <Save className="w-4 h-4 mr-2" />
              {loading ? t('common.loading') : t('common.save')}
            </Button>
            <Button 
              onClick={() => {
                setFormData(buildFormData());
                setIsEditing(true);
              }}
              variant="outline"
              className="flex-1 text-white border-gray-600"
            >
              {t('common.cancel')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Avatar Upload Modal */}
      <Dialog open={showAvatarModal} onOpenChange={setShowAvatarModal}>
        <DialogContent className="bg-gray-900 border-gray-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Actualizar Foto de Perfil</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {avatarPreview && (
              <div className="text-center">
                <img
                  src={avatarPreview}
                  alt="Preview"
                  className="w-32 h-32 rounded-full mx-auto object-cover border-4 border-amber-600"
                />
              </div>
            )}
            
            <div className="flex gap-2">
              <Button 
                onClick={handleAvatarUpload}
                disabled={loading || !avatarFile}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                <Upload className="w-4 h-4 mr-2" />
                {loading ? 'Subiendo...' : 'Subir Foto'}
              </Button>
              <Button 
                onClick={() => {
                  setShowAvatarModal(false);
                  setAvatarFile(null);
                  setAvatarPreview(null);
                }}
                variant="outline"
                className="flex-1 text-white border-gray-600"
              >
                <X className="w-4 h-4 mr-2" />
                Cancelar
              </Button>
            </div>
            
            <p className="text-xs text-gray-400 text-center">
              Formatos soportados: JPG, PNG. Máximo 5MB.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};